import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { CustomerProfileResponse, ProfileProgressEvent } from '../model/CustomerProfileTypes';

export interface PrefetchSnapshot {
  streamingText: string;
  progress: ProfileProgressEvent | null;
  data: CustomerProfileResponse | null;
  error: string | null;
  isStreaming: boolean;
  isConnecting: boolean;
}

interface CustomerProfilePrefetchState {
  customerId: string | null;
  lastConsumedId: string | null;
  lastConsumedSnapshot: PrefetchSnapshot | null;
  streamingText: string;
  progress: ProfileProgressEvent | null;
  data: CustomerProfileResponse | null;
  isStreaming: boolean;
  isConnecting: boolean;
  error: string | null;
  _abortController: AbortController | null;
}

interface CustomerProfilePrefetchActions {
  initPrefetch: (customerId: string, controller: AbortController) => void;
  setConnected: () => void;
  appendToken: (text: string) => void;
  setProgress: (event: ProfileProgressEvent) => void;
  setComplete: (data: CustomerProfileResponse) => void;
  setError: (msg: string) => void;
  cancelAndReset: () => void;
  clearLastConsumed: () => void;
  resetAll: () => void;
}

const initialState: CustomerProfilePrefetchState = {
  customerId: null,
  lastConsumedId: null,
  lastConsumedSnapshot: null,
  streamingText: '',
  progress: null,
  data: null,
  isStreaming: false,
  isConnecting: false,
  error: null,
  _abortController: null,
};

export const useCustomerProfilePrefetchStore = create<
  CustomerProfilePrefetchState & CustomerProfilePrefetchActions
>()(
  devtools(
    (set, get) => ({
      ...initialState,

      initPrefetch: (customerId, controller) => {
        // 이전 prefetch가 있으면 abort
        const prev = get()._abortController;
        if (prev && !prev.signal.aborted) {
          prev.abort();
        }
        set({
          ...initialState,
          customerId,
          isConnecting: true,
          _abortController: controller,
        });
      },

      setConnected: () => set({ isConnecting: false, isStreaming: true }),

      appendToken: (text) => set((state) => ({ streamingText: state.streamingText + text })),

      setProgress: (event) => set({ progress: event }),

      setComplete: (data) => set({ data, isStreaming: false }),

      setError: (msg) => set({ error: msg, isStreaming: false, isConnecting: false }),

      cancelAndReset: () => {
        const { _abortController, data, customerId, streamingText, progress, error, lastConsumedId, lastConsumedSnapshot } = get();
        if (_abortController && !_abortController.signal.aborted) {
          _abortController.abort();
        }
        const newLastConsumedId = data ? customerId : lastConsumedId;
        const newSnapshot: PrefetchSnapshot | null = data
          ? { streamingText, progress, data, error, isStreaming: false, isConnecting: false }
          : lastConsumedSnapshot;
        set({ ...initialState, lastConsumedId: newLastConsumedId, lastConsumedSnapshot: newSnapshot });
      },

      clearLastConsumed: () => set({ lastConsumedId: null, lastConsumedSnapshot: null }),

      resetAll: () => {
        const { _abortController } = get();
        if (_abortController && !_abortController.signal.aborted) {
          _abortController.abort();
        }
        set(initialState);
      },
    }),
    { name: 'CustomerProfilePrefetchStore' },
  ),
);
